import React, { useState, useRef, useEffect } from 'react';
import { CheckSquare, ChevronDown, X, Loader2, Users, Clock, AlertTriangle, AlertCircle, Shield, Calendar, UserX, FileWarning, Zap, List, Filter, Mail, XCircle, AlertOctagon } from 'lucide-react';

export interface QuickSelectOption {
    label: string;
    value: string;
    description?: string;
}

export interface QuickSelectGroup {
    label: string;
    options: QuickSelectOption[];
    isFooter?: boolean;
    mode?: 'check' | 'consent';
}

interface SelectionToolbarProps {
    selectedCount: number;
    totalFilteredCount: number;
    isSelectAllMode: boolean;
    isLoading: boolean;
    isStartingOperation: boolean;
    selectionMode: 'check' | 'consent';
    onClearSelection: () => void;
    onQuickSelect: (option: string) => void;
    onStartBulkCheck: () => void;
    onStartBulkConsent: () => void;
    quickSelectGroups: QuickSelectGroup[];
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
    selectedCount,
    totalFilteredCount,
    isSelectAllMode,
    isLoading,
    isStartingOperation,
    selectionMode,
    onClearSelection,
    onQuickSelect,
    onStartBulkCheck,
    onStartBulkConsent,
    quickSelectGroups
}) => {
    const [showQuickSelect, setShowQuickSelect] = useState(false);
    const [dropdownTab, setDropdownTab] = useState<'check' | 'consent'>('check');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowQuickSelect(false);
            }
        };

        if (showQuickSelect) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showQuickSelect]);

    if (selectedCount === 0) return null;

    const estimatedTime = Math.ceil(selectedCount * 0.5);

    const checkGroups = quickSelectGroups.filter(g => !g.isFooter && g.mode !== 'consent');
    const consentGroups = quickSelectGroups.filter(g => !g.isFooter && g.mode === 'consent');
    const footerGroup = quickSelectGroups.find(g => g.isFooter);
    const activeGroups = dropdownTab === 'check' ? checkGroups : consentGroups;

    // Icon mapping for options
    const getOptionIcon = (value: string) => {
        const iconClass = "w-4 h-4 flex-shrink-0";
        switch (value) {
            case 'high_risk': return <AlertTriangle className={`${iconClass} text-red-500`} />;
            case 'medium_risk': return <AlertCircle className={`${iconClass} text-amber-500`} />;
            case 'low_risk': return <Shield className={`${iconClass} text-green-500`} />;
            case 'due': return <Calendar className={`${iconClass} text-blue-500`} />;
            case 'never_checked': return <UserX className={`${iconClass} text-gray-500`} />;
            case 'critical_issues': return <FileWarning className={`${iconClass} text-red-600`} />;
            case 'high_risk_due': return <Zap className={`${iconClass} text-red-500`} />;
            case 'medium_risk_due': return <Zap className={`${iconClass} text-amber-500`} />;
            case 'no_consent': return <AlertOctagon className={`${iconClass} text-gray-500`} />;
            case 'expired_consent': return <XCircle className={`${iconClass} text-red-500`} />;
            case 'expiring_consent': return <AlertTriangle className={`${iconClass} text-yellow-500`} />;
            case 'current_page': return <List className={`${iconClass} text-indigo-500`} />;
            case 'all_drivers': return <Users className={`${iconClass} text-indigo-500`} />;
            case 'all_filtered': return <Filter className={`${iconClass} text-indigo-500`} />;
            default: return <CheckSquare className={`${iconClass} text-gray-400`} />;
        }
    };

    const formatLabel = (label: string) => {
        return label
            .replace('Select All ', '')
            .replace(' on Current Page', 'Current Page')
            .replace('All Filtered', 'All Filtered');
    };

    return (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40" ref={dropdownRef}>
            {/* Dropdown - positioned above the toolbar, centered */}
            {showQuickSelect && (
                <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl text-gray-900 border border-gray-200 overflow-hidden" style={{ minWidth: '660px' }}>
                    {/* Tab header */}
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => setDropdownTab('check')}
                            className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                                dropdownTab === 'check'
                                    ? 'text-gray-900 border-b-2 border-gray-900 bg-white'
                                    : 'text-gray-500 hover:text-gray-700 bg-gray-50'
                            }`}
                        >
                            Licence Check
                        </button>
                        <button
                            onClick={() => setDropdownTab('consent')}
                            className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                                dropdownTab === 'consent'
                                    ? 'text-gray-900 border-b-2 border-gray-900 bg-white'
                                    : 'text-gray-500 hover:text-gray-700 bg-gray-50'
                            }`}
                        >
                            Consent
                        </button>
                    </div>

                    {/* Main grid - active tab groups */}
                    <div className="grid divide-x divide-gray-100" style={{ gridTemplateColumns: `repeat(${activeGroups.length}, 1fr)` }}>
                        {activeGroups.map((group) => (
                            <div key={group.label} className="p-5" style={{ minWidth: '180px' }}>
                                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                    {group.label}
                                </div>
                                <div className="space-y-1">
                                    {group.options.map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onQuickSelect(option.value);
                                                setShowQuickSelect(false);
                                            }}
                                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 rounded-lg transition-all flex items-center space-x-3 group"
                                        >
                                            {getOptionIcon(option.value)}
                                            <span className="font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap">{formatLabel(option.label)}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer - scope pills */}
                    {footerGroup && (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center space-x-2">
                                {footerGroup.options.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            onQuickSelect(option.value);
                                            setShowQuickSelect(false);
                                        }}
                                        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 transition-all flex items-center space-x-1.5 whitespace-nowrap"
                                    >
                                        {getOptionIcon(option.value)}
                                        <span>{formatLabel(option.label)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}


            {/* Toolbar */}
            <div className="bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-4 flex items-center space-x-6 border border-gray-700">
                {/* Selection Count */}
                <div className="flex items-center space-x-3 flex-shrink-0">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: '#69BE28' }}>
                        <Users className="w-5 h-5" />
                    </div>
                    <div className="whitespace-nowrap">
                        <p className="text-sm font-semibold">
                            {isSelectAllMode
                                ? `All ${totalFilteredCount} drivers selected`
                                : `${selectedCount} driver${selectedCount !== 1 ? 's' : ''} selected`
                            }
                        </p>
                        <p className="text-xs text-gray-400 flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            Est. ~{estimatedTime} min{estimatedTime !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                {/* Divider */}
                <div className="h-10 w-px bg-gray-700" />

                {/* Quick Select Button */}
                <button
                    onClick={() => setShowQuickSelect(!showQuickSelect)}
                    disabled={isLoading}
                    className={`px-4 py-2 text-sm rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50 border whitespace-nowrap flex-shrink-0 ${
                        showQuickSelect
                            ? 'bg-gray-700 border-gray-500 text-white'
                            : 'bg-gray-800 hover:bg-gray-700 border-gray-600'
                    }`}
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Loading...</span>
                        </>
                    ) : (
                        <>
                            <CheckSquare className="w-4 h-4" />
                            <span>Quick Select</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showQuickSelect ? 'rotate-180' : ''}`} />
                        </>
                    )}
                </button>

                {/* Clear Selection */}
                <button
                    onClick={onClearSelection}
                    disabled={isStartingOperation}
                    className="px-3 py-2 text-sm text-gray-400 hover:text-white font-medium transition-colors flex items-center space-x-1 disabled:opacity-50"
                >
                    <X className="w-4 h-4" />
                    <span>Clear</span>
                </button>

                {/* Divider */}
                <div className="h-10 w-px bg-gray-700" />

                {/* Context-aware action button based on active dropdown tab */}
                {dropdownTab === 'consent' ? (
                    <button
                        onClick={() => { setShowQuickSelect(false); onStartBulkConsent(); }}
                        disabled={isStartingOperation || isLoading}
                        className="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center space-x-2 hover:brightness-110 bg-blue-600 whitespace-nowrap flex-shrink-0"
                    >
                        {isStartingOperation ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Mail className="w-4 h-4" />
                                <span>Send Consent Forms</span>
                            </>
                        )}
                    </button>
                ) : (
                    <button
                        onClick={onStartBulkCheck}
                        disabled={isStartingOperation || isLoading}
                        className="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center space-x-2 hover:brightness-110 whitespace-nowrap flex-shrink-0"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isStartingOperation ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Starting...</span>
                            </>
                        ) : (
                            <span>Run Licence Check</span>
                        )}
                    </button>
                )}

            </div>
        </div>
    );
};

export default SelectionToolbar;
